import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Mail, Send, HelpCircle, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

const supportCategories = [
  { value: "billing", label: "Billing & Payments" },
  { value: "technical", label: "Technical Issue" },
  { value: "account", label: "Account & Login" },
  { value: "feature", label: "Feature Request" },
  { value: "feedback", label: "General Feedback" },
  { value: "other", label: "Other" },
];

const faqItems = [
  {
    question: "How do I create my first faceless video script?",
    answer: "Head to Nexis AI from the sidebar, click 'Create New Project', and follow the step-by-step wizard. You'll input your niche, choose a visual style, and Nexis will generate a complete script with image and video prompts."
  },
  {
    question: "What's the difference between Nexis AI, Monetization AI, and AskFint?",
    answer: "Nexis AI creates faceless video scripts and prompts. Monetization AI helps you build digital products like courses, ebooks, and Creator Swipe Files to sell. AskFint is your personal AI mentor that answers questions about content creation and the platform."
  },
  {
    question: "How do I cancel or change my subscription?",
    answer: "Go to Settings → Manage Subscription to access the billing portal. From there, you can cancel, upgrade, or update your payment method."
  },
  {
    question: "Where can I find tutorials to get started?",
    answer: "Visit the Tutorials section in the sidebar under 'Learn'. We have step-by-step guides for creating scripts, generating prompts, and monetizing your content."
  },
  {
    question: "How do I use the image and video prompts from Nexis AI?",
    answer: "After generating prompts in Nexis AI, copy them and paste into AI image generators like Midjourney, DALL-E, or Leonardo AI. For video prompts, use tools like Runway, Pika, or Kling AI."
  },
  {
    question: "What is your refund policy?",
    answer: "Due to the nature of our digital products and instant access to all tools and training, all sales are final. Please review the program details carefully before purchasing to ensure it's the right fit for you."
  },
  {
    question: "How do I reset my password?",
    answer: "On the login page, click 'Forgot Password' and enter your email. You'll receive a password reset link. If you're already logged in, go to Settings to update your password."
  },
  {
    question: "What file formats does Monetization AI support?",
    answer: "Monetization AI helps you structure your content — for ebooks you'll get a complete outline and chapters, for courses you'll get module structures. The final export depends on the delivery platform you choose."
  },
];

const Support = () => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: user?.email || "",
    category: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.category || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.functions.invoke("send-support-email", {
        body: {
          email: formData.email,
          category: formData.category,
          message: formData.message,
        },
      });

      if (error) throw error;

      toast.success("Your message has been sent! We'll get back to you soon.");
      setFormData({
        email: user?.email || "",
        category: "",
        message: "",
      });
    } catch (error) {
      console.error("Error sending support message:", error);
      toast.error("Failed to send message. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 md:space-y-8 max-w-3xl mx-auto px-1 sm:px-0">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-foreground">Support</h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">
          Need help? We're here for you. Reach out and we'll get back to you as soon as possible.
        </p>
      </div>

      {/* Contact Email Card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 p-4 sm:p-6">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-xs sm:text-sm text-muted-foreground">Email us directly at</p>
            <a 
              href="mailto:support@ai-storycraft-studio.com" 
              className="text-base sm:text-lg font-semibold text-primary hover:underline break-all"
            >
              support@ai-storycraft-studio.com
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Support Form */}
      <Card>
        <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            Send us a message
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Fill out the form below and we'll respond within 24-48 hours.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-2 sm:pt-4">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {/* Email */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="email" className="text-sm">Your Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-background h-10 sm:h-11 text-sm sm:text-base"
              />
            </div>

            {/* Category */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="category" className="text-sm">What is this about?</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="bg-background h-10 sm:h-11 text-sm sm:text-base">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {supportCategories.map((category) => (
                    <SelectItem key={category.value} value={category.value} className="text-sm sm:text-base">
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Message */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="message" className="text-sm">Your Message</Label>
              <Textarea
                id="message"
                placeholder="Describe your issue or question in detail..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="min-h-[120px] sm:min-h-[150px] bg-background text-sm sm:text-base"
              />
            </div>

            {/* Submit */}
            <Button type="submit" className="w-full h-10 sm:h-11 text-sm sm:text-base" disabled={isSubmitting}>
              {isSubmitting ? (
                "Sending..."
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card>
        <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            Frequently Asked Questions
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Quick answers to common questions
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left hover:no-underline text-sm sm:text-base py-3 sm:py-4">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-xs sm:text-sm pb-3 sm:pb-4">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* AskFint Hint */}
      <Card className="border-muted">
        <CardContent className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6">
          <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="font-medium text-foreground text-sm sm:text-base">Quick tip</p>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              For faster answers, try asking <span className="text-primary font-medium">AskFint</span> — our AI assistant can help with most questions about the platform.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Support;
